import React from 'react';

import { NextPageWithLayout } from 'next';

import { AppDownloadSection } from '../../../components/AppDownloadSection/AppDownloadSection';
import { MetaTags } from '../../../components/MetaTags/MetaTags';
import { CircleImageAndText } from '../components/CircleImageAndText';

import phoneOne from './assets/phoneOne.png';
import phoneThree from './assets/phoneThree.png';
import phoneFour from './assets/phoneFour.png';
import phoneTwo from './assets/phoneTwo.png';
import phoneFive from './assets/phoneFive.png';
import { HeaderSection } from '../components/HeaderSection';
import {
  META_FISHING_LOG_BOOK_TITLE,
  META_FISHING_LOG_BOOK_DESCRIPTION,
  META_FISHING_LOG_BOOK_CANONICAL,
} from '../../../constants/metadata';
import { Container } from '../../../components/Container/Container';
import { Heading } from '../../../components/Heading/Heading';
import { Text } from '../../../components/Text/Text';
import { CatchIconGreen } from '../../../components/Icons/CatchIconGreen';
import { CatchIconRed } from '../../../components/Icons/CatchIconRed';

export const LogbookPage: NextPageWithLayout = () => {
  return (
    <div>
      <MetaTags
        title={META_FISHING_LOG_BOOK_TITLE}
        description={META_FISHING_LOG_BOOK_DESCRIPTION}
        link={META_FISHING_LOG_BOOK_CANONICAL}
      />
      <HeaderSection
        headingText="Fishing logbook - Gain fishing insights"
        bodyText="A fishing logbook helps keep track of what kind of fish you caught, when you caught it,
          and where you caught it. You can also log the gear you used to catch it. All of this data
          helps you spot patterns in your fishing so you can become a better angler."
      />
      <CircleImageAndText
        imgSrc={phoneOne}
        imgAlt="Your personal fishing logbook with the Fishbrain app"
        headingText="Your personal fishing logbook"
        bodyText="Logging your catches is key to improving your skills. With the Fishbrain App, you can
          track a ton of information. Check out the data you can remember with every catch below:"
      >
        <ul className="my-4 list-inside list-disc">
          <li className="text-sm leading-3 sm:text-base sm:leading-4">Species</li>
          <li className="text-sm leading-3 sm:text-base sm:leading-4">Date & time</li>
          <li className="text-sm leading-3 sm:text-base sm:leading-4">Size</li>
          <li className="text-sm leading-3 sm:text-base sm:leading-4">Location & water</li>
          <li className="text-sm leading-3 sm:text-base sm:leading-4">Gear used</li>
          <li className="text-sm leading-3 sm:text-base sm:leading-4">Weather & solunar data</li>
        </ul>
      </CircleImageAndText>

      <CircleImageAndText
        imgSrc={phoneTwo}
        imgRight
        imgAlt="How do you track your fishing history?"
        headingText="How do you track your fishing history?"
        bodyText="Once you upload your catch to Fishbrain, you will be prompted to add a photo of your catch to commemorate it, as well as share it with the community (if you choose to)."
      />

      <div className="bg-lake-fog">
        <Container className="mt-md py-sm md:py-md flex flex-col sm:flex-row md:justify-center">
          <div className="sm:w-col-6 md:w-col-5">
            <Heading variant="md" className="mt-4 mr-24">
              Set your privacy level
            </Heading>
            <Text variant="lg" className="mt-4 mb-5">
              With Fishbrain, you can select the privacy level you choose for every catch you log.
              We make sure you never have to give away locations unless you decide to do so.
            </Text>
          </div>

          <div className="sm:w-col-6 md:w-col-5 sm:pl-8">
            <div className="mb-2 flex">
              <div>
                <CatchIconGreen className="mr-2" />
              </div>
              <div>
                <Text className="mb-0 font-semibold">Public catch location</Text>
                <Text>Exact location and water name is visible to everyone</Text>
              </div>
            </div>
            <div className="mb-2 flex">
              <div>
                <CatchIconRed className="mr-2" />
              </div>
              <div>
                <Text className="mb-0 font-semibold">Only share water name</Text>
                <Text>Exact location is only visible to you, water name is public</Text>
              </div>
            </div>
            <div className="flex">
              <div>
                <CatchIconRed className="mr-2" />
              </div>
              <div>
                <Text className="mb-0 font-semibold">Private - only share catch details</Text>
                <Text>Visible to only you</Text>
              </div>
            </div>
          </div>
        </Container>
      </div>

      <CircleImageAndText
        imgSrc={phoneThree}
        imgAlt="Keep track of your catches in one place with the Fishbrain app"
        headingText="Keep track of your catches in one place"
        bodyText="Track your species count, weight, and length of your catches and log the baits you use by saving your catch details in your profile."
      />

      <CircleImageAndText
        imgSrc={phoneFour}
        imgRight
        imgAlt="How can a logbook improve your fishing?"
        headingText="How can a logbook improve your fishing?"
        bodyText="It's common for an angler to think they will remember the details of each fish they caught, but in practice, it's difficult to store all of this information for long periods of time. Keeping a fishing logbook helps you test which bait is more effective when catching particular species, spot patterns, fishing tactics, and more."
      />

      <CircleImageAndText
        imgSrc={phoneFive}
        imgAlt="Learn from personal insights with the Fishbrain app"
        headingText="Learn from personal insights"
        bodyText="With Fishbrain Pro, you can access your personal statistics to see what conditions lead to your best performance. Fish like a Pro with access to your most active month, moon phases, and weather conditions."
      />

      <AppDownloadSection />
    </div>
  );
};
